import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, FileText, Star, FileType, Download, CheckSquare, Trash, Tags, GripVertical, Menu, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLocation } from "wouter";
import JournalEditor from "@/components/journal-editor";
import JournalSearchFilter, { type JournalFilters } from "@/components/journal-search-filter";
import JournalSearch from "./journal-search";
import JournalAnalytics from "./journal-analytics";
import { apiRequest } from "@/lib/queryClient";
import { exportMultipleEntries, getDefaultPDFConfig } from "@/lib/exportUtils";
import { useToast } from "@/hooks/use-toast";
import type { JournalEntry, JournalStats, JournalTemplate } from "@shared/schema";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export default function JournalPage() {
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  
  const urlParams = new URLSearchParams(window.location.search);
  const goalIdFromUrl = urlParams.get('goalId');

  const activeTab = location.includes('/analytics') ? 'analytics' : 
                   location.includes('/search') ? 'search' : 'entries';
  
  const handleTabChange = (value: string) => {
    if (value === 'analytics') setLocation('/journal/analytics');
    else if (value === 'search') setLocation('/journal/search');
    else setLocation('/journal');
  };
  
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [filters, setFilters] = useState<JournalFilters>({
    search: "",
    tags: [],
    mood: "",
    weather: "",
    category: "",
    startDate: undefined,
    endDate: undefined,
    pinnedOnly: false,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'markdown' | 'json'>('pdf');
  const [pdfOrientation, setPdfOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [pdfPageSize, setPdfPageSize] = useState<'letter' | 'a4' | 'legal'>('letter');
  const [pdfMarginTop, setPdfMarginTop] = useState(25.4);
  const [pdfMarginBottom, setPdfMarginBottom] = useState(25.4);
  const [pdfMarginLeft, setPdfMarginLeft] = useState(25.4);
  const [pdfMarginRight, setPdfMarginRight] = useState(25.4);
  const [pdfStylePreset, setPdfStylePreset] = useState<'professional' | 'minimal' | 'creative' | 'academic'>('professional');
  const [pdfLayoutTemplate, setPdfLayoutTemplate] = useState<'standard' | 'compact' | 'summary'>('standard');
  const [pdfIncludeCover, setPdfIncludeCover] = useState(false);
  const [pdfCoverTitle, setPdfCoverTitle] = useState('Journal Entries');
  const [pdfIncludeTOC, setPdfIncludeTOC] = useState(false);
  const [pdfMetadataPosition, setPdfMetadataPosition] = useState<'top' | 'bottom'>('top');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [tagsToAdd, setTagsToAdd] = useState('');
  const [tagsToRemove, setTagsToRemove] = useState('');
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const queryClient = useQueryClient();

  const { data: entries = [], isLoading } = useQuery<JournalEntry[]>({
    queryKey: [
      "/api/journal", 
      filters.search, 
      filters.tags.join(","),
      filters.mood,
      filters.weather,
      filters.category,
      filters.startDate?.toISOString(),
      filters.endDate?.toISOString(),
      filters.pinnedOnly
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.tags.length > 0) params.append("tags", filters.tags.join(","));
      if (filters.mood) params.append("mood", filters.mood);
      if (filters.weather) params.append("weather", filters.weather);
      if (filters.category) params.append("category", filters.category);
      if (filters.startDate) params.append("startDate", filters.startDate.toISOString());
      if (filters.endDate) params.append("endDate", filters.endDate.toISOString());
      if (filters.pinnedOnly) params.append("pinnedOnly", "true");
      
      const response = await fetch(`/api/journal?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to fetch entries');
      return response.json();
    },
  });

  const { data: stats } = useQuery<JournalStats>({
    queryKey: ["/api/stats/journal"],
  });

  const { data: allTags = [] } = useQuery<string[]>({
    queryKey: ["/api/journal/tags"],
  });

  const { data: categories = [] } = useQuery<{ id: string; name: string; color: string }[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  useEffect(() => {
    if (goalIdFromUrl) {
      setIsCreating(true);
      setSelectedEntry(null);
    }
  }, [goalIdFromUrl]);

  const createEntryMutation = useMutation({
    mutationFn: async (entry: { title: string; content: any; plainContent: string; tags: string[]; category?: string; goalId?: string | null }) => {
      const response = await fetch("/api/journal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
      });
      if (!response.ok) throw new Error('Failed to create entry');
      return response.json();
    },
    onSuccess: (newEntry) => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/journal"] });
      setSelectedEntry(newEntry);
      setIsCreating(false);
      toast({
        title: "Entry created",
        description: "Your journal entry has been saved successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Failed to save journal entry. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<JournalEntry> }) => {
      const response = await fetch(`/api/journal/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error('Failed to update entry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/journal"] });
      toast({
        title: "Entry saved",
        description: "Your changes have been saved.",
      });
    },
    onError: () => {
      toast({
        title: "Save failed",
        description: "Failed to save journal entry. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteEntryMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error('Failed to delete entry');
      // 204 No Content responses have no body to parse
      if (response.status === 204) return;
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/journal"] });
      toast({
        title: "Entry deleted",
        description: "Journal entry has been successfully deleted.",
      });
      // Delay closing the editor to allow toast to render
      setTimeout(() => setSelectedEntry(null), 100);
    },
    onError: (error) => {
      toast({
        title: "Delete failed",
        description: "Failed to delete journal entry. Please try again.",
        variant: "destructive",
      });
    },
  });

  const { data: templates = [] } = useQuery<JournalTemplate[]>({
    queryKey: ["/api/journal/templates"],
  });

  const pinMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/${id}/pin`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to pin entry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
    },
    onError: () => {
    },
  });

  const unpinMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/${id}/pin`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to unpin entry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
    },
    onError: () => {
    },
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const response = await fetch(`/api/journal/from-template/${templateId}`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to create entry from template');
      return response.json();
    },
    onSuccess: (newEntry: JournalEntry) => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      setSelectedEntry(newEntry);
      setShowTemplates(false);
    },
    onError: () => {
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (entryIds: string[]) => {
      const response = await fetch('/api/journal/batch', {
        method: 'DELETE',
        body: JSON.stringify({ entryIds }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Delete failed');
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/journal"] });
      setSelectedEntries(new Set());
    },
    onError: () => {
    }
  });

  const bulkTagMutation = useMutation({
    mutationFn: async ({ entryIds, tagsToAdd, tagsToRemove }: { entryIds: string[], tagsToAdd: string[], tagsToRemove: string[] }) => {
      const response = await fetch('/api/journal/batch/tags', {
        method: 'PATCH',
        body: JSON.stringify({ entryIds, tagsToAdd, tagsToRemove }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Tag update failed');
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      queryClient.invalidateQueries({ queryKey: ["/api/journal/tags"] });
      setSelectedEntries(new Set());
      setTagsToAdd('');
      setTagsToRemove('');
      setShowTagDialog(false);
    },
    onError: () => {
    }
  });

  const reorderEntriesMutation = useMutation({
    mutationFn: async (updates: Array<{ id: string; displayOrder: number }>) => {
      const response = await fetch('/api/journal/reorder', {
        method: 'POST',
        body: JSON.stringify({ updates }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Reorder failed');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
    },
  });

  const handleCreateEntry = () => {
    setIsCreating(true);
    setSelectedEntry(null);
  };

  const handleSaveEntry = (entryData: { title: string; content: any; plainContent: string; tags: string[]; category?: string; goalId?: string | null }) => {
    toast({
      title: "Saving...",
      description: "Your journal entry is being saved.",
    });
    if (isCreating) {
      createEntryMutation.mutate(entryData);
    } else if (selectedEntry) {
      updateEntryMutation.mutate({
        id: selectedEntry.id,
        updates: entryData,
      });
    }
  };

  const handleSelectEntry = (entry: JournalEntry) => {
    setSelectedEntry(entry);
    setIsCreating(false);
  };

  const handleDeleteEntry = (id: string) => {
    if (confirm("Are you sure you want to delete this journal entry?")) {
      deleteEntryMutation.mutate(id);
    }
  };

  const handleTogglePin = (e: React.MouseEvent, entry: JournalEntry) => {
    e.stopPropagation();
    if (entry.isPinned) {
      unpinMutation.mutate(entry.id);
    } else {
      pinMutation.mutate(entry.id);
    }
  };

  const handleToggleSelection = (checked: string | boolean, entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (checked === true) {
      newSelected.add(entryId);
    } else {
      newSelected.delete(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEntries.size === sortedEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(sortedEntries.map(e => e.id)));
    }
  };

  const handleExportSelected = async () => {
    const entriesToExport = entries.filter(e => selectedEntries.has(e.id));
    if (entriesToExport.length === 0) {
      return;
    }
    
    try {
      // Build complete PDF config by merging user settings with defaults
      const pdfConfig = {
        ...getDefaultPDFConfig(),
        orientation: pdfOrientation,
        pageSize: pdfPageSize,
        margins: {
          top: pdfMarginTop,
          bottom: pdfMarginBottom,
          left: pdfMarginLeft,
          right: pdfMarginRight,
        },
        stylePreset: pdfStylePreset,
        layoutTemplate: pdfLayoutTemplate,
        includeCoverPage: pdfIncludeCover,
        ...(pdfIncludeCover ? {
          coverPage: {
            title: pdfCoverTitle,
            showDateRange: true,
            showEntryCount: true,
          },
        } : {}),
        includeTableOfContents: pdfIncludeTOC,
        ...(pdfIncludeTOC ? {
          tocOptions: {
            title: 'Table of Contents',
            showPageNumbers: true,
            showDates: true,
            clickableLinks: false,
          },
        } : {}),
        metadataOptions: {
          ...getDefaultPDFConfig().metadataOptions,
          position: pdfMetadataPosition,
        },
      };
      
      await exportMultipleEntries(entriesToExport, exportFormat, pdfConfig);
      setShowExportDialog(false);
      setSelectedEntries(new Set());
    } catch (error) {
    }
  };

  const handleBulkDelete = async () => {
    await bulkDeleteMutation.mutateAsync(Array.from(selectedEntries));
    setShowDeleteDialog(false);
  };

  const handleBulkTag = async () => {
    const addTags = tagsToAdd.split(',').map(t => t.trim()).filter(Boolean);
    const removeTags = tagsToRemove.split(',').map(t => t.trim()).filter(Boolean);
    
    await bulkTagMutation.mutateAsync({
      entryIds: Array.from(selectedEntries),
      tagsToAdd: addTags,
      tagsToRemove: removeTags
    });
  };

  // Sort entries: pinned first, then by displayOrder, then by creation date
  const sortedEntries = [...entries].sort((a, b) => {
    // Pinned items always on top
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    
    // Within pinned or unpinned groups, sort by displayOrder
    const orderA = a.displayOrder ?? 0;
    const orderB = b.displayOrder ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    
    // If same displayOrder, sort by creation date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  // Sortable Card Component
  function SortableJournalCard({ entry }: { entry: JournalEntry }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: entry.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div ref={setNodeRef} style={style}>
        <Card
          className={`cursor-pointer transition-colors hover:bg-accent ${
            selectedEntry?.id === entry.id ? "ring-2 ring-primary" : ""
          }`}
          onClick={() => handleSelectEntry(entry)}
        >
          <CardContent className="p-3">
            <div className="flex items-start gap-2 mb-1">
              {/* Drag handle */}
              <div
                {...attributes}
                {...listeners}
                className="mt-0.5 cursor-grab active:cursor-grabbing touch-manipulation"
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              <Checkbox
                checked={selectedEntries.has(entry.id)}
                onCheckedChange={(checked) => handleToggleSelection(checked, entry.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`checkbox-entry-${entry.id}`}
                className="mt-0.5"
              />
              <div className="flex items-start justify-between flex-1">
                <h3 className="font-semibold text-sm line-clamp-1 flex-1">{entry.title}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 ml-2"
                  onClick={(e) => handleTogglePin(e, entry)}
                  data-testid={`button-pin-${entry.id}`}
                >
                  <Star className={`h-4 w-4 ${entry.isPinned ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
              {entry.plainContent}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1">
                {entry.category && (
                  <Badge 
                    variant="secondary" 
                    className="text-xs"
                    data-testid={`badge-category-${entry.id}`}
                  >
                    {entry.category}
                  </Badge>
                )}
                {entry.tags.slice(0, 2).map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
                {entry.tags.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{entry.tags.length - 2}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {new Date(entry.updatedAt).toLocaleDateString()}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sortedEntries.findIndex(entry => entry.id === active.id);
    const newIndex = sortedEntries.findIndex(entry => entry.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const movedEntry = sortedEntries[oldIndex];
    const targetEntry = sortedEntries[newIndex];

    // Prevent moving unpinned items into pinned section and vice versa
    if (movedEntry.isPinned !== targetEntry.isPinned) {
      toast({
        title: "Cannot move",
        description: "Pinned and unpinned entries must stay in their respective sections.",
        variant: "destructive",
      });
      return;
    }

    // Create new array with reordered items
    const newSortedEntries = [...sortedEntries];
    newSortedEntries.splice(oldIndex, 1);
    newSortedEntries.splice(newIndex, 0, movedEntry);

    // Calculate new display orders for all items in the same pinned/unpinned group
    const updates: Array<{ id: string; displayOrder: number }> = [];
    let displayOrder = 0;

    newSortedEntries.forEach(entry => {
      if (entry.isPinned === movedEntry.isPinned) {
        updates.push({ id: entry.id, displayOrder });
        displayOrder++;
      }
    });

    // Optimistically update the UI
    queryClient.setQueryData<JournalEntry[]>(
      ["/api/journal", filters.search, filters.tags.join(","), filters.mood, filters.weather, filters.category, filters.startDate?.toISOString(), filters.endDate?.toISOString(), filters.pinnedOnly],
      (oldEntries) => {
        if (!oldEntries) return oldEntries;
        return oldEntries.map(entry => {
          const update = updates.find(u => u.id === entry.id);
          return update ? { ...entry, displayOrder: update.displayOrder } : entry;
        });
      }
    );

    // Persist to backend
    reorderEntriesMutation.mutate(updates);
  };

  return (
    <div className="flex h-screen w-full bg-background">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex-1 w-full flex flex-col">
        <div className="border-b px-4 sm:px-6 pt-4 pb-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-4">
            <div className="flex items-center gap-3">
              <Sheet open={showMobileSidebar} onOpenChange={setShowMobileSidebar}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="icon" 
                    className="lg:hidden"
                    data-testid="button-mobile-menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0 flex flex-col">
                  {/* Mobile Sidebar Content */}
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b">
                      {/* Prominent New Entry Button */}
                      <Button 
                        onClick={() => {
                          handleCreateEntry();
                          setShowMobileSidebar(false);
                        }}
                        className="w-full mb-4"
                        size="lg"
                        data-testid="button-create-entry-mobile"
                      >
                        <Plus className="h-5 w-5 mr-2" />
                        New Entry
                      </Button>

                      {/* Action Buttons */}
                      {selectedEntries.size > 0 ? (
                        <div className="flex gap-2 mb-4">
                          <Button 
                            onClick={() => {
                              setShowExportDialog(true);
                              setShowMobileSidebar(false);
                            }}
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            data-testid="button-export-selected-mobile"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Export ({selectedEntries.size})
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setShowDeleteDialog(true);
                              setShowMobileSidebar(false);
                            }}
                            className="flex-1"
                            data-testid="button-delete-selected-mobile"
                          >
                            <Trash className="h-4 w-4 mr-1" />
                            Delete ({selectedEntries.size})
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowTagDialog(true);
                              setShowMobileSidebar(false);
                            }}
                            className="flex-1"
                            data-testid="button-tag-selected-mobile"
                          >
                            <Tags className="h-4 w-4 mr-1" />
                            Tag ({selectedEntries.size})
                          </Button>
                        </div>
                      ) : (
                        <div className="mb-4">
                          <Button 
                            onClick={() => {
                              setShowTemplates(true);
                              setShowMobileSidebar(false);
                            }}
                            size="sm"
                            variant="outline"
                            className="w-full"
                            data-testid="button-templates-mobile"
                          >
                            <FileType className="h-4 w-4 mr-2" />
                            Templates
                          </Button>
                        </div>
                      )}
                  
                      {/* Select All - shown only when there are entries */}
                      {sortedEntries.length > 0 && (
                        <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                          <Checkbox
                            checked={selectedEntries.size === sortedEntries.length && sortedEntries.length > 0}
                            onCheckedChange={handleSelectAll}
                            data-testid="checkbox-select-all-mobile"
                          />
                          <label className="text-sm text-muted-foreground cursor-pointer" onClick={handleSelectAll}>
                            {selectedEntries.size === sortedEntries.length && sortedEntries.length > 0 
                              ? 'Deselect All' 
                              : 'Select All'}
                          </label>
                        </div>
                      )}

                      {/* Search and Filter */}
                      <div className="mb-4">
                        <JournalSearchFilter
                          filters={filters}
                          onFiltersChange={setFilters}
                          availableTags={allTags}
                          categories={categories}
                        />
                      </div>

                      {/* Stats */}
                      {stats && (
                        <div className="grid grid-cols-2 gap-2 text-center">
                          <div className="bg-muted/50 rounded-lg p-2">
                            <div className="text-lg font-bold text-primary">{stats.totalEntries}</div>
                            <div className="text-xs text-muted-foreground">Total Entries</div>
                          </div>
                          <div className="bg-muted/50 rounded-lg p-2">
                            <div className="text-lg font-bold text-primary">{stats.writingStreak}</div>
                            <div className="text-xs text-muted-foreground">Day Streak</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Entry List */}
                    <div className="flex-1 overflow-y-auto">
                      {isLoading ? (
                        <div className="p-4 text-center text-muted-foreground">Loading entries...</div>
                      ) : sortedEntries.length === 0 ? (
                        <div className="p-4 text-center text-muted-foreground">
                          {filters.search || filters.tags.length > 0 || filters.mood || filters.weather || filters.category || filters.startDate || filters.endDate
                            ? "No entries match your filters" 
                            : "No journal entries yet"}
                        </div>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={sortedEntries.map(entry => entry.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2 p-2">
                              {sortedEntries.map((entry: JournalEntry) => (
                                <div 
                                  key={entry.id} 
                                  onClick={() => {
                                    handleSelectEntry(entry);
                                    setShowMobileSidebar(false);
                                  }}
                                >
                                  <SortableJournalCard entry={entry} />
                                </div>
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Journal
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Record your thoughts and track your journey
                </p>
              </div>
            </div>
          </div>
          <TabsList data-testid="tabs-journal">
            <TabsTrigger value="entries" data-testid="tab-entries">Entries</TabsTrigger>
            <TabsTrigger value="search" data-testid="tab-search">Search</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="entries" className="flex-1 w-full flex overflow-hidden mt-0 p-0">
          <div className="flex-1 w-full flex overflow-hidden">
            {/* Desktop Sidebar - hidden on mobile, collapsible */}
            <div 
              className={`hidden lg:flex border-r bg-card flex-col relative transition-all duration-300 ease-in-out ${
                sidebarCollapsed ? 'w-16' : 'w-80'
              }`}
            >
              {/* Collapse/Expand Toggle Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="absolute -right-3 top-4 z-10 h-6 w-6 rounded-full border bg-background shadow-md hover:bg-accent"
                data-testid="button-toggle-sidebar"
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>

              {/* Collapsed View - Icons Only */}
              {sidebarCollapsed ? (
                <div className="flex flex-col items-center py-4 gap-3">
                  <Button
                    onClick={handleCreateEntry}
                    size="icon"
                    className="h-10 w-10"
                    data-testid="button-create-entry-collapsed"
                    title="New Entry"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                  <Button
                    onClick={() => setShowTemplates(true)}
                    size="icon"
                    variant="outline"
                    className="h-10 w-10"
                    data-testid="button-templates-collapsed"
                    title="Templates"
                  >
                    <FileType className="h-5 w-5" />
                  </Button>
                  <Button
                    onClick={() => setSidebarCollapsed(false)}
                    size="icon"
                    variant="ghost"
                    className="h-10 w-10"
                    data-testid="button-search-collapsed"
                    title="Search & Filter"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                  {selectedEntries.size > 0 && (
                    <>
                      <Separator className="w-8" />
                      <Button
                        onClick={() => setShowExportDialog(true)}
                        size="icon"
                        variant="outline"
                        className="h-10 w-10"
                        data-testid="button-export-collapsed"
                        title={`Export (${selectedEntries.size})`}
                      >
                        <Download className="h-5 w-5" />
                      </Button>
                      <Button
                        onClick={() => setShowTagDialog(true)}
                        size="icon"
                        variant="outline"
                        className="h-10 w-10"
                        data-testid="button-tag-collapsed"
                        title={`Tag (${selectedEntries.size})`}
                      >
                        <Tags className="h-5 w-5" />
                      </Button>
                      <Button
                        onClick={() => setShowDeleteDialog(true)}
                        size="icon"
                        variant="destructive"
                        className="h-10 w-10"
                        data-testid="button-delete-collapsed"
                        title={`Delete (${selectedEntries.size})`}
                      >
                        <Trash className="h-5 w-5" />
                      </Button>
                    </>
                  )}
                  <Separator className="w-8" />
                  {/* Stats in collapsed view */}
                  {stats && (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="bg-muted/50 rounded-lg p-2 w-12">
                        <div className="text-sm font-bold text-primary">{stats.totalEntries}</div>
                        <div className="text-[10px] text-muted-foreground">Entries</div>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-2 w-12">
                        <div className="text-sm font-bold text-primary">{stats.writingStreak}</div>
                        <div className="text-[10px] text-muted-foreground">Streak</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Header - Expanded View */}
                  <div className="p-4 border-b">
                    {/* Prominent New Entry Button */}
                    <Button 
                      onClick={handleCreateEntry}
                      className="w-full mb-4"
                      size="lg"
                      data-testid="button-create-entry"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      New Entry
                    </Button>

                    {/* Action Buttons */}
                    {selectedEntries.size > 0 ? (
                      <div className="flex gap-2 mb-4">
                        <Button 
                          onClick={() => setShowExportDialog(true)}
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          data-testid="button-export-selected"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Export ({selectedEntries.size})
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setShowDeleteDialog(true)}
                          className="flex-1"
                          data-testid="button-delete-selected"
                        >
                          <Trash className="h-4 w-4 mr-1" />
                          Delete ({selectedEntries.size})
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTagDialog(true)}
                          className="flex-1"
                          data-testid="button-tag-selected"
                        >
                          <Tags className="h-4 w-4 mr-1" />
                          Tag ({selectedEntries.size})
                        </Button>
                      </div>
                    ) : (
                      <div className="mb-4">
                        <Button 
                          onClick={() => setShowTemplates(true)}
                          size="sm"
                          variant="outline"
                          className="w-full"
                          data-testid="button-templates"
                        >
                          <FileType className="h-4 w-4 mr-2" />
                          Templates
                        </Button>
                      </div>
                    )}
                
                    {/* Select All - shown only when there are entries */}
                    {sortedEntries.length > 0 && (
                      <div className="flex items-center gap-2 mb-4 pb-2 border-b">
                        <Checkbox
                          checked={selectedEntries.size === sortedEntries.length && sortedEntries.length > 0}
                          onCheckedChange={handleSelectAll}
                          data-testid="checkbox-select-all"
                        />
                        <label className="text-sm text-muted-foreground cursor-pointer" onClick={handleSelectAll}>
                          {selectedEntries.size === sortedEntries.length && sortedEntries.length > 0 
                            ? 'Deselect All' 
                            : 'Select All'}
                        </label>
                      </div>
                    )}

                    {/* Search and Filter */}
                    <div className="mb-4">
                      <JournalSearchFilter
                        filters={filters}
                        onFiltersChange={setFilters}
                        availableTags={allTags}
                        categories={categories}
                      />
                    </div>

                    {/* Stats */}
                    {stats && (
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="bg-muted/50 rounded-lg p-2">
                          <div className="text-lg font-bold text-primary">{stats.totalEntries}</div>
                          <div className="text-xs text-muted-foreground">Total Entries</div>
                        </div>
                        <div className="bg-muted/50 rounded-lg p-2">
                          <div className="text-lg font-bold text-primary">{stats.writingStreak}</div>
                          <div className="text-xs text-muted-foreground">Day Streak</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Entry List */}
                  <div className="flex-1 overflow-y-auto">
                    {isLoading ? (
                      <div className="p-4 text-center text-muted-foreground">Loading entries...</div>
                    ) : sortedEntries.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">
                        {filters.search || filters.tags.length > 0 || filters.mood || filters.weather || filters.category || filters.startDate || filters.endDate
                          ? "No entries match your filters" 
                          : "No journal entries yet"}
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={sortedEntries.map(entry => entry.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-2 p-2">
                            {sortedEntries.map((entry: JournalEntry) => (
                              <SortableJournalCard key={entry.id} entry={entry} />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                </>
              )}
            </div>

        {/* Main Content */}
        <div className="flex-1 w-full flex flex-col overflow-y-auto p-6">
          {selectedEntry || isCreating ? (
            <JournalEditor
              key={selectedEntry?.id || 'new-entry'}
              entry={selectedEntry}
              isCreating={isCreating}
              initialGoalId={isCreating ? goalIdFromUrl : undefined}
              onSave={handleSaveEntry}
              onDelete={selectedEntry ? () => handleDeleteEntry(selectedEntry.id) : undefined}
              onCancel={() => {
                setSelectedEntry(null);
                setIsCreating(false);
              }}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Welcome to your Journal</h2>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Start writing to capture your thoughts, track your progress, and reflect on your journey.
                  Use AI features to get insights and suggestions.
                </p>
                <Button onClick={handleCreateEntry} data-testid="button-create-first-entry">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Entry
                </Button>
              </div>
            </div>
          )}
        </div>
          </div>
        </TabsContent>

        <TabsContent value="search" className="flex-1 overflow-auto mt-0 p-4">
          <JournalSearch />
        </TabsContent>

        <TabsContent value="analytics" className="flex-1 overflow-auto mt-0 p-4">
          <JournalAnalytics />
        </TabsContent>
      </Tabs>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Journal Templates</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {templates.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No templates available yet</p>
            ) : (
              <div className="grid gap-4">
                {templates.map((template) => (
                  <Card
                    key={template.id}
                    className="cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => createFromTemplateMutation.mutate(template.id)}
                    data-testid={`template-${template.id}`}
                  >
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center justify-between">
                        <span>{template.name}</span>
                        <Badge variant={template.isDefault ? "default" : "outline"}>
                          {template.category}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{template.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Export Journal Entries</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                Export {selectedEntries.size} selected {selectedEntries.size === 1 ? 'entry' : 'entries'} in your preferred format.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium">Export Format</label>
                <Select value={exportFormat} onValueChange={(value: 'pdf' | 'markdown' | 'json') => setExportFormat(value)}>
                  <SelectTrigger data-testid="select-export-format">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF Document</SelectItem>
                    <SelectItem value="markdown">Markdown File</SelectItem>
                    <SelectItem value="json">JSON Data</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* PDF Configuration Options */}
            {exportFormat === 'pdf' && (
              <div className="space-y-6 border-t pt-6">
                <h3 className="text-lg font-semibold">PDF Configuration</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Orientation */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Orientation</label>
                    <Select value={pdfOrientation} onValueChange={(value: 'portrait' | 'landscape') => setPdfOrientation(value)}>
                      <SelectTrigger data-testid="select-pdf-orientation">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="portrait">Portrait</SelectItem>
                        <SelectItem value="landscape">Landscape</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Page Size */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Page Size</label>
                    <Select value={pdfPageSize} onValueChange={(value: 'letter' | 'a4' | 'legal') => setPdfPageSize(value)}>
                      <SelectTrigger data-testid="select-pdf-page-size">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="letter">Letter (8.5" × 11")</SelectItem>
                        <SelectItem value="a4">A4 (210mm × 297mm)</SelectItem>
                        <SelectItem value="legal">Legal (8.5" × 14")</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Margins */}
                <div className="space-y-4">
                  <label className="text-sm font-medium">Margins (mm)</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Top</label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="1"
                        value={pdfMarginTop}
                        onChange={(e) => setPdfMarginTop(parseFloat(e.target.value) || 0)}
                        data-testid="input-pdf-margin-top"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Bottom</label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="1"
                        value={pdfMarginBottom}
                        onChange={(e) => setPdfMarginBottom(parseFloat(e.target.value) || 0)}
                        data-testid="input-pdf-margin-bottom"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Left</label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="1"
                        value={pdfMarginLeft}
                        onChange={(e) => setPdfMarginLeft(parseFloat(e.target.value) || 0)}
                        data-testid="input-pdf-margin-left"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs text-muted-foreground">Right</label>
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        step="1"
                        value={pdfMarginRight}
                        onChange={(e) => setPdfMarginRight(parseFloat(e.target.value) || 0)}
                        data-testid="input-pdf-margin-right"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Standard margins: 25.4mm (1 inch). Adjust for your preferences.
                  </p>
                </div>

                {/* Quick Margin Presets */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Margin Presets</label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPdfMarginTop(25.4);
                        setPdfMarginBottom(25.4);
                        setPdfMarginLeft(25.4);
                        setPdfMarginRight(25.4);
                      }}
                      data-testid="button-preset-normal"
                    >
                      Normal (1")
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPdfMarginTop(12.7);
                        setPdfMarginBottom(12.7);
                        setPdfMarginLeft(12.7);
                        setPdfMarginRight(12.7);
                      }}
                      data-testid="button-preset-narrow"
                    >
                      Narrow (0.5")
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPdfMarginTop(50.8);
                        setPdfMarginBottom(50.8);
                        setPdfMarginLeft(50.8);
                        setPdfMarginRight(50.8);
                      }}
                      data-testid="button-preset-wide"
                    >
                      Wide (2")
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowExportDialog(false)}
                data-testid="button-cancel-export"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleExportSelected}
                data-testid="button-confirm-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedEntries.size} {selectedEntries.size === 1 ? 'entry' : 'entries'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the selected journal {selectedEntries.size === 1 ? 'entry' : 'entries'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tag Management Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Tags for {selectedEntries.size} {selectedEntries.size === 1 ? 'Entry' : 'Entries'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="tags-add">Add Tags</Label>
              <Input
                id="tags-add"
                value={tagsToAdd}
                onChange={(e) => setTagsToAdd(e.target.value)}
                placeholder="Enter tags (comma-separated)"
                data-testid="input-tags-add"
              />
              <p className="text-xs text-muted-foreground">
                Add multiple tags separated by commas (e.g., work, important, review)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tags-remove">Remove Tags</Label>
              <Input
                id="tags-remove"
                value={tagsToRemove}
                onChange={(e) => setTagsToRemove(e.target.value)}
                placeholder="Enter tags to remove (comma-separated)"
                data-testid="input-tags-remove"
              />
              <p className="text-xs text-muted-foreground">
                Remove specific tags from all selected entries
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowTagDialog(false)}
              data-testid="button-cancel-tag"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBulkTag}
              data-testid="button-confirm-tag"
            >
              Apply Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}